import { Injectable } from '@angular/core'
import { filter } from 'rxjs/operators'
import { GoRealtimeClient } from './go-realtime.client'

@Injectable({ providedIn: 'root' })
export class RealtimeSubscriptionRegistry {
  private readonly sets = new Map<string, Set<number>>()
  private lastRequested: number[] = []
  private readonly assetSets = new Map<string, Set<number>>()
  private lastRequestedAssets: number[] = []

  constructor(private client: GoRealtimeClient) {
    // resubscribe on every (re)connect — server has no session resume
    this.client.state$.pipe(filter((s) => s === 'connected')).subscribe(() => {
      this.flush()
      this.flushAssets()
    })
    // log the applied subset so silently-dropped ids are visible (no retry needed — server already returned survivors)
    this.client.ack$.pipe(filter((a) => a?.action === 'subscribed')).subscribe((a) => this.onSubscribed(a))
  }

  register(consumerId: string, vehicleIds: number[]): void {
    this.sets.set(consumerId, new Set(vehicleIds))
    this.flush()
  }

  release(consumerId: string): void {
    this.sets.delete(consumerId)
    this.flush()
  }

  registerAssets(consumerId: string, assetIds: number[]): void {
    this.assetSets.set(consumerId, new Set(assetIds))
    this.flushAssets()
  }

  releaseAssets(consumerId: string): void {
    this.assetSets.delete(consumerId)
    this.flushAssets()
  }

  private union(): number[] {
    const all = new Set<number>()
    for (const s of this.sets.values()) for (const id of s) all.add(id)
    return [...all]
  }

  private unionAssets(): number[] {
    const all = new Set<number>()
    for (const s of this.assetSets.values()) for (const id of s) all.add(id)
    return [...all]
  }

  private flush(): void {
    const vehicleIds = this.union()
    this.lastRequested = vehicleIds
    // safe to re-send a stored set — server returns the survivors (Q9)
    if (vehicleIds.length) this.client.send({ action: 'subscribe', type: 'vehicle', vehicleIds })
  }

  private flushAssets(): void {
    const assetIds = this.unionAssets()
    this.lastRequestedAssets = assetIds
    // v2-parity action name (BE handleSubscribeAssets); server returns the applied subset (Q9)
    if (assetIds.length) this.client.send({ action: 'subscribeToAssets', assetIds })
  }

  private onSubscribed(a: any): void {
    // asset ack carries data.assetIds (may be null when empty); vehicle ack carries data.vehicleIds
    const isAsset = a?.data && 'assetIds' in a.data
    const applied: number[] = a?.data?.vehicleIds ?? a?.data?.assetIds ?? []
    const requested = isAsset ? this.lastRequestedAssets : this.lastRequested
    const dropped = requested.filter((id) => !applied.includes(id))
    if (dropped.length) {
      console.warn(`[realtime] ${isAsset ? 'asset ' : ''}subscribe applied subset; dropped ids:`, dropped)
    }
  }
}
